import CheckIcon from "~icons/local/check";
import { RequestedPartDetail } from "@/lib/queries/types";
import { QuoteRevision } from "@/lib/queries/types";
import { Page } from "@/types";
import usePage from "@/lib/usePage";
import { useParams } from "react-router";
import useToPage from "@/lib/useToPage";
import PartStatusIcon from "@/lib/PartStatusIcon";
import {
  FlatRequestedPartDetail,
  flatParts,
  truncateTextMiddle,
} from "@/lib/utils";
import useGetCurrentQuoteRevision from "../SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import AccountingSystemDisplay from "./Accounting";
import { Tooltip } from "react-tooltip";

export default function Steps() {
  const { toRfq, toQuote, toSend } = useToPage();
  const page = usePage();
  const quoteRevision = useGetCurrentQuoteRevision();
  if (!quoteRevision || !page) return null;
  const { requested_part_revisions, status } = quoteRevision;

  return (
    <div className="flex flex-col py-4">
      <Dot
        className="mr-[-6px]"
        label="Estimate"
        complete={["estimated", "quote_created", "sent_quote"].includes(status)}
        active={[Page.rfq, Page.part].includes(page)}
        onClick={() => toRfq()}
      />
      <Link
        active={
          [Page.rfq, Page.part].includes(page) ||
          ["new", "estimated", "quote_created", "sent_quote"].includes(status)
        }
        parts={requested_part_revisions}
      />
      <Dot
        label="Quote"
        complete={["quote_created", "sent_quote"].includes(status)}
        active={page === Page.quote}
        disabled={
          !["estimated", "quote_created", "sent_quote", "new"].includes(status)
        }
        onClick={toQuote}
      />
      <Link
        active={
          [Page.send, Page.done].includes(page) ||
          ["quote_created", "sent_quote"].includes(status)
        }
        quoteRevision={quoteRevision}
      />
      <Dot
        className="ml-[1px]"
        label="Send"
        complete={["sent_quote"].includes(status)}
        active={[Page.send, Page.done].includes(page)}
        disabled={!["quote_created", "sent_quote"].includes(status)}
        onClick={toSend}
      />
    </div>
  );
}

function Dot({
  label,
  active,
  complete,
  disabled = false,
  className = "",
  onClick,
}: {
  label: string;
  active: boolean;
  complete: boolean;
  disabled?: boolean;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <button
      disabled={disabled}
      type="button"
      className={`flex gap-1 items-center cursor-pointer disabled:cursor-default group ${className}`}
      onClick={onClick}
    >
      <div
        className={`h-6 w-6 rounded-full flex items-center justify-center bg-blue-600 group-disabled:bg-slate-300 ${
          active && "border-2 border-sky-400"
        }`}
      >
        {complete && <CheckIcon />}
      </div>
      <div className="text-sm text-slate-900">{label}</div>
    </button>
  );
}

function Link({
  active,
  parts = [],
  quoteRevision,
}: {
  active: boolean;
  parts?: RequestedPartDetail[];
  quoteRevision?: QuoteRevision;
}) {
  const partList = flatParts(parts);

  return (
    <div
      className={`min-h-16 translate-x-[10px] border-l-[5px] ${
        active ? "border-l-blue-600" : "border-l-slate-300"
      } flex flex-col gap-2 pl-3 py-3`}
    >
      {partList.map((part) => (
        <PartDisplay key={part.id} part={part} />
      ))}
      {quoteRevision && (
        <AccountingSystemDisplay quoteRevision={quoteRevision} />
      )}
    </div>
  );
}

function PartDisplay({ part }: { part: FlatRequestedPartDetail }) {
  const tooltipId = Math.random().toString();
  const { toPart } = useToPage();
  const { partId } = useParams();
  const { id, part_number, will_bid, level } = part;
  const fullLabel = part_number;
  const truncatedLabel = truncateTextMiddle(fullLabel, 16);

  return (
    <>
      <div
        className={`rounded-sm cursor-pointer hover:bg-blue-100 ${
          partId === id && "bg-blue-200"
        } py-1 flex items-center gap-2`}
        style={{ paddingLeft: 5 + level * 17 }}
        onClick={() => toPart(id)}
        data-tooltip-id={tooltipId}
      >
        <PartStatusIcon willBid={will_bid} className="min-w-[14px]" />
        <div className="text-sm">{truncatedLabel}</div>
      </div>
      {fullLabel !== truncatedLabel && (
        <Tooltip id={tooltipId} style={{ width: "100%" }}>
          <span className="text-xs">{fullLabel}</span>
        </Tooltip>
      )}
    </>
  );
}
